import { useEffect, useMemo, useState } from 'react'
import { useForm, Controller, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useOnboardingStore, STEPS } from '../stores/onboardingStore'
import { useStepSubmit } from '../context/StepSubmitContext'
import SegmentedYesNo from '../components/form/SegmentedYesNo'
import FieldGroup from '../components/form/FieldGroup'
import ChipSelect from '../components/form/ChipSelect'
import Field from '../components/form/Field'
import FieldRow from '../components/form/FieldRow'
import Card from '../components/form/Card'
import StepLayout from '../components/form/StepLayout'
import Band from '../components/form/Band'
import ErrorBanner from '../components/form/ErrorBanner'
import { useStepErrorSummary } from '../hooks/useStepErrorSummary'
import StripePaymentModal from '../components/form/StripePaymentModal'
import StripeCardOnlyModal from '../components/form/StripeCardOnlyModal'
import { flattenErrorCount } from '../lib/validators'
import { isValidationDisabled } from '../lib/validationMode'
import { apiPost } from '../lib/api'

const STEP_ID = 'billing'

// Card-on-file lives here (moved from Owners) so all payment setup sits in one
// place. Same $1-hold verification + saved-PM flow as before.
const SS_SESSION = 'brite-onboarding-session-id'
function getSessionId() {
  if (typeof sessionStorage === 'undefined') return undefined
  return sessionStorage.getItem(SS_SESSION) || undefined
}

function formatBrand(brand) {
  if (!brand) return 'Card'
  return brand.charAt(0).toUpperCase() + brand.slice(1)
}

function formatExpiry(expMonth, expYear) {
  const mm = String(expMonth || '').padStart(2, '0')
  const yy = String(expYear || '').slice(-2)
  return `${mm}/${yy}`
}

// Apply the practice's hormone discount to a "$1,247.00"-style price string.
// Returns { original, discounted } — discounted is null when pct<=0 or the
// string isn't parseable, so callers render the original untouched.
function applyHormoneDiscount(priceStr, pct) {
  const original = String(priceStr ?? '')
  const num = Number(original.replace(/[$,]/g, ''))
  if (!(pct > 0) || !Number.isFinite(num)) return { original, discounted: null }
  const discounted =
    '$' +
    (num * (1 - pct / 100)).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  return { original, discounted }
}

// A pricing-table price cell: the plain price when there's no discount, else the
// original struck through (reusing PlatformFeeCard's price-was class) followed
// by the discounted teal price.
function PriceCell({ value, pct }) {
  const { original, discounted } = applyHormoneDiscount(value, pct)
  if (!discounted) return original
  return (
    <>
      <span className="price-was">{original}</span>
      <span style={{ color: 'var(--accent)', fontWeight: 500 }}>{discounted}</span>
    </>
  )
}

const strictSchema = z.object({
  payForNonSubscriptionMedications: z.boolean({
    required_error: 'Please answer this question',
    invalid_type_error: 'Please answer this question',
  }),
  payForShippingCharges: z.boolean({
    required_error: 'Please answer this question',
    invalid_type_error: 'Please answer this question',
  }),
  practiceMarkup: z
    .number({ invalid_type_error: 'Enter a number between 0 and 100' })
    .min(0, 'Markup must be 0 or higher')
    .max(100, 'Markup cannot exceed 100%'),
  labOrderPayer: z.enum(['Any', 'Patient', 'Practice'], {
    required_error: 'Please select a payer',
    invalid_type_error: 'Please select a payer',
  }),
  preferredShipping: z.enum(['usps_priority', 'ups_2nd_day']).optional().default('usps_priority'),
})
const looseSchema = z.object({
  payForNonSubscriptionMedications: z.any(),
  payForShippingCharges: z.any(),
  practiceMarkup: z.any(),
  labOrderPayer: z.any(),
  preferredShipping: z.any(),
}).passthrough()
const schema = isValidationDisabled() ? looseSchema : strictSchema

const LAB_PAYER_OPTIONS = [
  { value: 'Patient', label: 'Patient pays' },
  { value: 'Practice', label: 'Practice pays' },
  { value: 'Any', label: 'Decide per order' },
]

const WOMEN_PRICING = [
  { count: '1 hormone', monthly: '$47', annual: '$463.00' },
  { count: '2 hormones', monthly: '$74', annual: '$744.00' },
  { count: '3 hormones', monthly: '$112', annual: '$1,115.00' },
  { count: '4 hormones', monthly: '$124', annual: '$1,247.00' },
]

const MEN_PRICING = [
  { hormone: 'Testosterone', monthly: '$87', annual: '$872.00' },
]

// Personalization-aware variant of the BRITE Platform Monthly Fee card.
// Non-standard buckets render the standard $174/mo struck through next to the
// personalized rate so the practice sees what they would have paid.
function PlatformFeeCard({ discountBucket, practiceName }) {
  const isPersonalized =
    discountBucket && discountBucket !== 'standard'
  const cardStyle = isPersonalized
    ? {
        borderLeft: '3px solid var(--accent)',
        boxShadow: '-1px 0 12px -8px var(--accent)',
      }
    : undefined

  const newPriceStyle = { color: 'var(--accent)', fontWeight: 500 }

  // Two-line trial layout (used by trial_then_99 and trial_then_174).
  // showWasRate=true adds struck-through "$174/mo" before the new rate on the
  // "then …" line — only used when the trial-end rate differs from standard.
  // "per provider" forced to its own line so the strikethrough + new price
  // stay grouped on a single line.
  const trialPrice = (rate, showWasRate) => (
    <div className="price-row" style={{ alignItems: 'flex-start' }}>
      <span>Per provider · billed when active</span>
      <div style={{ textAlign: 'right' }}>
        <div style={{ color: 'var(--accent)', fontWeight: 500 }}>
          Free for 6 months
        </div>
        <div
          style={{
            color: 'var(--muted)',
            fontSize: 13,
            marginTop: 4,
            lineHeight: 1.4,
          }}
        >
          then {showWasRate && <span className="price-was">$174/mo</span>}
          <span style={newPriceStyle}>{rate}/mo</span>
          <br />per provider
        </div>
      </div>
    </div>
  )

  let priceBlock
  let note
  switch (discountBucket) {
    case 'small_practice':
      priceBlock = (
        <div className="price-row">
          <span>Per provider · billed when active</span>
          <span>
            <span className="price-was">$174/mo</span>
            <span style={newPriceStyle}>$99/mo</span>
          </span>
        </div>
      )
      note =
        'Charged $99/month per provider, beginning only when a provider has active patients. If a provider has no active patients, the monthly fee pauses — please notify us, as we won\'t know otherwise.'
      break
    case 'trial_then_99':
      priceBlock = trialPrice('$99', true)
      note =
        'For your first 6 months on BRITE, the platform fee is free. After that, you\'ll be charged $99/month per provider, beginning only when a provider has active patients. If a provider has no active patients, the monthly fee pauses — please notify us.'
      break
    case 'trial_then_174':
      // Trial-end rate matches standard — the discount IS the trial period
      // itself (already shown on the top line), so no strikethrough needed.
      priceBlock = trialPrice('$174', false)
      note =
        'For your first 6 months on BRITE, the platform fee is free. After that, you\'ll be charged $174/month per provider, beginning only when a provider has active patients. If a provider has no active patients, the monthly fee pauses — please notify us.'
      break
    case 'permanent_waiver':
      priceBlock = (
        <div className="price-row">
          <span>Per provider · billed when active</span>
          <span>
            <span className="price-was">$174/mo</span>
            <span style={newPriceStyle}>Waived</span>
          </span>
        </div>
      )
      note =
        'The platform fee is waived for your practice. There\'s no monthly per-provider charge.'
      break
    default:
      // 'standard' or unknown bucket — render the original card unchanged.
      priceBlock = (
        <div className="platform-fee-hero">
          <span className="platform-fee-price">$174/mo</span>
          <span className="platform-fee-subtitle">Per provider · billed when active</span>
        </div>
      )
      note =
        'Charged $174/month per provider, beginning only when a provider has active patients. If a provider has no active patients, the monthly fee pauses — please notify us, as we won\'t know otherwise.'
  }

  return (
    <div
      className={`info-block${isPersonalized ? ' is-personalized' : ''}`}
      style={cardStyle}
    >
      <h3 className="info-block-title">BRITE Platform Monthly Fee</h3>
      {isPersonalized && (
        <div className="personalized-eyebrow">
          {practiceName ? (
            <>
              Reserved for{' '}
              <span className="personalized-eyebrow-name">{practiceName}</span>
            </>
          ) : (
            'Your rate'
          )}
        </div>
      )}
      {priceBlock}
      <p className="info-block-note">{note}</p>
    </div>
  )
}

export default function Step2Billing() {
  const step = STEPS.find((s) => s.id === 'billing')
  const persisted = useOnboardingStore((s) => s.formData[STEP_ID])
  const personalization = useOnboardingStore((s) => s.personalization)
  // Practice hormone discount (applied to BOTH monthly + annual on every row).
  const hormonePct = Number(personalization?.hormoneDiscountPct) || 0
  const liteMode = useOnboardingStore((s) => s.liteMode)
  const updateFormData = useOnboardingStore((s) => s.updateFormData)
  const nextStep = useOnboardingStore((s) => s.nextStep)
  const { setHandler, clearHandler, reportErrorCount, pulse, focusFirstError } =
    useStepSubmit()

  // ── Card-on-file (relocated from Owners) ──
  const stripeSaved = useOnboardingStore((s) => s.formData?.stripe)
  const ownersList = useOnboardingStore((s) => s.formData?.owners?.owners)
  // The per-provider platform fee only applies when the practice actually has
  // providers. Zero providers (common under Network+) → hide the fee block.
  const providersList = useOnboardingStore((s) => s.formData?.providers?.providers)
  const hasProviders = (providersList?.length ?? 0) > 0
  const [payModal, setPayModal] = useState(null)
  const [payBusy, setPayBusy] = useState(false)
  const [payError, setPayError] = useState('')
  const [paymentBlockError, setPaymentBlockError] = useState('')

  const hasSavedCard = Boolean(
    stripeSaved?.paymentMethodId && stripeSaved?.card?.last4,
  )
  const firstOwner = (ownersList || [])[0] || {}
  const owner1Ready =
    String(firstOwner.fullName || '').trim().length > 0 &&
    String(firstOwner.email || '').trim().length > 0
  const cardButtonDisabled = !owner1Ready || payBusy

  const beginCardVerification = async () => {
    if (payBusy) return
    const fd = useOnboardingStore.getState().formData
    const first = fd?.owners?.owners?.[0] || {}
    const firstName = String(first.fullName || '').trim()
    const firstEmail = String(first.email || '').trim()
    if (!firstName || !firstEmail) {
      setPayError("Add an owner's name and email on the Owners step first.")
      return
    }
    setPayBusy(true)
    setPayError('')
    const res = await apiPost('/payment-intent', {
      email: firstEmail,
      name: firstName,
      sessionId: getSessionId(),
    })
    setPayBusy(false)
    if (!res.ok || !res.data?.clientSecret) {
      setPayError(
        res.error === 'timeout'
          ? 'Payment service timed out — try again.'
          : "Couldn't start card verification. Try again or contact support@brite.live.",
      )
      pulse()
      return
    }
    setPayModal({ clientSecret: res.data.clientSecret, finalAmountCents: 100 })
  }

  const handleModalSuccess = async (paymentIntent) => {
    try {
      const res = await apiPost('/save-payment-method', {
        paymentIntentId: paymentIntent.id,
        sessionId: getSessionId(),
      })
      if (!res.ok || !res.data?.paymentMethodId) {
        const msg = res.data?.error || res.error || 'unknown error'
        setPayError('Card charged but could not be saved: ' + msg)
        return
      }
      setTimeout(() => {
        updateFormData('stripe', {
          paymentMethodId: res.data.paymentMethodId,
          stripeCustomerId: res.data.customerId,
          card: res.data.card || null,
        })
        setPaymentBlockError('')
      }, 50)
    } catch (err) {
      setPayError('Save failed: ' + (err.message || 'unknown'))
    }
  }

  const removeSavedCard = () => {
    updateFormData('stripe', {
      paymentMethodId: null,
      stripeCustomerId: null,
      card: null,
    })
  }

  const defaultValues = useMemo(
    () => ({
      payForNonSubscriptionMedications:
        persisted?.payForNonSubscriptionMedications ?? undefined,
      payForShippingCharges: persisted?.payForShippingCharges ?? undefined,
      practiceMarkup:
        typeof persisted?.practiceMarkup === 'number'
          ? persisted.practiceMarkup
          : 0,
      labOrderPayer: persisted?.labOrderPayer ?? undefined,
      preferredShipping: persisted?.preferredShipping ?? 'usps_priority',
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const {
    control,
    handleSubmit,
    trigger,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  })

  // Lab markup only applies when the patient pays. When the practice covers the
  // lab cost directly there's no patient-facing markup, so the field is hidden.
  const labPayer = useWatch({ control, name: 'labOrderPayer' })
  const markupValue = Number(useWatch({ control, name: 'practiceMarkup' })) || 0
  const showMarkup = labPayer !== 'Practice'

  // Clear any stale markup when the practice switches to paying for labs
  // directly — prevents a hidden markup value from being saved.
  useEffect(() => {
    if (labPayer === 'Practice' && markupValue > 0) {
      setValue('practiceMarkup', 0, { shouldDirty: true, shouldValidate: true })
    }
  }, [labPayer, markupValue, setValue])


  const onValid = (data) => {
    setPaymentBlockError('')
    updateFormData(STEP_ID, data)
    // Credit card on file is required for every practice. The card may have
    // already been captured on the Network+ step (Skip-path $1 verify, or a
    // card-paid setup fee); here we enforce it for everyone else. Read fresh
    // store state so a just-saved card is seen (the handler closure is stale).
    const fresh = useOnboardingStore.getState().formData
    if (!isValidationDisabled() && !fresh?.stripe?.paymentMethodId) {
      setPaymentBlockError('Add a payment method to continue.')
      pulse()
      return
    }
    nextStep()
  }
  const { submitAttempted, errorMessages, onInvalid } = useStepErrorSummary(
    STEP_ID,
    errors,
  )

  useEffect(() => {
    const submit = handleSubmit(onValid, onInvalid)
    setHandler(() => submit())
    return () => setHandler(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSubmit, setHandler])

  useEffect(() => {
    reportErrorCount(flattenErrorCount(errors))
  }, [errors, reportErrorCount])

  useEffect(() => {
    return () => reportErrorCount(0)
  }, [reportErrorCount])

  useEffect(() => {
    if (persisted && Object.keys(persisted).length > 0) trigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <form
      className={`step-body step-wide billing-compact${liteMode ? ' is-lite' : ''}`}
      onSubmit={handleSubmit(onValid, onInvalid)}
      noValidate
    >
      <StepLayout question={step.title} helper={step.subtitle} className="is-wide">
      <Band label="Billing configuration" description="Payment and pricing setup">
      <ErrorBanner show={submitAttempted} messages={errorMessages} />
      <div className="billing-layout">
        <div className="billing-reference">
          <div className="step-section">
            <div className="info-block">
              <h3 className="info-block-title">
                Pricing for BRITE Subscription for Women
              </h3>
              <p className="info-block-subtitle">
                (Any Hormone Combination) · BRITE charges a monthly or yearly
                subscription based on the number of hormones prescribed, not
                per order.
              </p>
              {hormonePct > 0 && (
                <div className="hormone-discount-badge">
                  <span className="hormone-discount-badge-glyph" aria-hidden="true">✦</span>
                  Your practice: <strong>{hormonePct}% off</strong> all hormone subscriptions
                </div>
              )}
              <table className="pricing-table">
                <thead>
                  <tr>
                    <th>Count</th>
                    <th>Monthly</th>
                    <th>Annual (16.3% off)</th>
                  </tr>
                </thead>
                <tbody>
                  {WOMEN_PRICING.map((r) => (
                    <tr key={r.count}>
                      <td>{r.count}</td>
                      <td><PriceCell value={r.monthly} pct={hormonePct} /></td>
                      <td><PriceCell value={r.annual} pct={hormonePct} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="step-section">
            <div className="info-block">
              <h3 className="info-block-title">
                Pricing for BRITE Subscription for Men
              </h3>
              <p className="info-block-subtitle">
                Subscription pricing for men includes PDE5 inhibitors
                (Tadalafil/Sildenafil) when prescribed alongside testosterone
                — no additional charge. If a male patient is not on a hormone
                subscription and you prescribe only a PDE5 inhibitor, it is
                billed separately as an individual medication.
              </p>
              {hormonePct > 0 && (
                <div className="hormone-discount-badge">
                  <span className="hormone-discount-badge-glyph" aria-hidden="true">✦</span>
                  Your practice: <strong>{hormonePct}% off</strong> all hormone subscriptions
                </div>
              )}
              <table className="pricing-table">
                <thead>
                  <tr>
                    <th>Hormone</th>
                    <th>Monthly</th>
                    <th>Annual (16.3% off)</th>
                  </tr>
                </thead>
                <tbody>
                  {MEN_PRICING.map((r) => (
                    <tr key={r.hormone}>
                      <td>{r.hormone}</td>
                      <td><PriceCell value={r.monthly} pct={hormonePct} /></td>
                      <td><PriceCell value={r.annual} pct={hormonePct} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="step-section">
            <div className="info-row">
              <div className="info-block">
                <h3 className="info-block-title">Shipping Rates</h3>
                <div className="price-row">
                  <span>USPS Priority · 3–5 day</span>
                  <span>$14.95</span>
                </div>
                <div className="price-row">
                  <span>UPS 2nd Day Air · 2-day</span>
                  <span>$19.95</span>
                </div>
                <div className="shipping-selector">
                  <h4 className="shipping-selector-title">Default shipping method</h4>
                  <p className="shipping-selector-helper">Applied to all orders. Providers can change per order.</p>
                  <Controller
                    name="preferredShipping"
                    control={control}
                    render={({ field }) => (
                      <div className="shipping-options">
                        <button
                          type="button"
                          className={`shipping-option${field.value === 'usps_priority' ? ' is-selected' : ''}`}
                          onClick={() => field.onChange('usps_priority')}
                        >
                          USPS Priority
                        </button>
                        <button
                          type="button"
                          className={`shipping-option${field.value === 'ups_2nd_day' ? ' is-selected' : ''}`}
                          onClick={() => field.onChange('ups_2nd_day')}
                        >
                          UPS 2nd Day Air
                        </button>
                      </div>
                    )}
                  />
                </div>
                <p className="info-block-note">
                  Select who pays for shipping below. This is saved as your default. <em>To adjust later, reach out to support@brite.live.</em>
                </p>
              </div>
              {!liteMode && hasProviders && (
                <PlatformFeeCard
                  discountBucket={personalization?.discountBucket}
                  practiceName={personalization?.practiceName}
                />
              )}
            </div>
          </div>
        </div>

        <div className="billing-decisions">
          <Card title="Practice Payment Configuration">
            <FieldGroup name="payForNonSubscriptionMedications">
              <div className="question-label">
                Will the practice pay for medications not covered under subscriptions?
              </div>
              <p className="field-helper">
                <strong>Yes</strong> bills these charges to your practice. <strong>No</strong> means the patient pays for them at time of purchase. <em>This is set during onboarding. To adjust later, reach out to support@brite.live.</em>
              </p>
              <Controller
                name="payForNonSubscriptionMedications"
                control={control}
                render={({ field }) => (
                  <SegmentedYesNo
                    value={field.value}
                    onChange={field.onChange}
                    name="payForNonSubscriptionMedications"
                    error={errors.payForNonSubscriptionMedications}
                  />
                )}
              />
              {errors.payForNonSubscriptionMedications && (
                <div className="field-error">
                  {errors.payForNonSubscriptionMedications.message}
                </div>
              )}
            </FieldGroup>

            <FieldGroup name="payForShippingCharges">
              <div className="question-label" style={{ marginTop: 24 }}>
                Will the practice pay for shipping charges?
              </div>
              <p className="field-helper">
                <strong>Yes</strong> covers shipping on patient orders by default. <strong>No</strong> means the patient pays. <em>This is set during onboarding. To adjust later, reach out to support@brite.live.</em>
              </p>
              <Controller
                name="payForShippingCharges"
                control={control}
                render={({ field }) => (
                  <SegmentedYesNo
                    value={field.value}
                    onChange={field.onChange}
                    name="payForShippingCharges"
                    error={errors.payForShippingCharges}
                  />
                )}
              />
              {errors.payForShippingCharges && (
                <div className="field-error">
                  {errors.payForShippingCharges.message}
                </div>
              )}
            </FieldGroup>
          </Card>

          <Card
            title="Test Ordering & Practice Markup"
            description="Order LabCorp blood tests and the Meridian Valley 24-Hour Female Urine Hormone Test directly through BRITE at discounted rates. Set your markup to decide your commission per test order."
          >

            <div className="partner-lockup">
              <div className="partner-lockup-label">Available through</div>
              <div className="partner-lockup-logos">
                <img
                  src="/partners/labcorp.png"
                  alt="LabCorp"
                  className="partner-logo partner-logo--labcorp"
                />
                <img
                  src="/partners/meridian-valley.png"
                  alt="Meridian Valley Laboratories"
                  className="partner-logo partner-logo--meridian"
                />
              </div>
            </div>

            <FieldGroup name="labOrderPayer">
              <Field
                label="Who pays for lab testing?"
                error={errors.labOrderPayer}
              >
                <p className="field-helper">
                  {labPayer === 'Patient' && 'Patient is charged at the time of order. Your commission percentage (below) is added on top of the lab cost.'}
                  {labPayer === 'Practice' && 'Your practice covers the full lab cost. No commission is applied — you handle reimbursement on your end.'}
                  {labPayer === 'Any' && 'You choose per order at checkout whether the patient or practice pays.'}
                  {!labPayer && 'This determines who gets billed when a lab test is ordered through BRITE. Set during onboarding — to change later, email support@brite.live.'}
                </p>
                <Controller
                  name="labOrderPayer"
                  control={control}
                  render={({ field }) => (
                    <ChipSelect
                      options={LAB_PAYER_OPTIONS}
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      error={errors.labOrderPayer}
                    />
                  )}
                />
              </Field>
            </FieldGroup>

            {showMarkup && (
            <FieldGroup name="practiceMarkup">
              <Field
                label="Your lab test commission (%)"
                htmlFor="practiceMarkup"
                error={errors.practiceMarkup}
                help="When patients pay for lab tests, this percentage is added on top of the base lab cost — credited back to your practice as revenue. Most set 10–20%. Max $85 per test."
              >
                <Controller
                  name="practiceMarkup"
                  control={control}
                  render={({ field }) => (
                    <input
                      id="practiceMarkup"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      data-lpignore="true"
                      data-form-type="other"
                      className={`field-input${errors.practiceMarkup ? ' has-error' : ''}`}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value)}
                      onBlur={() => {
                        const n = Number(field.value)
                        if (field.value === '' || field.value == null || Number.isNaN(n)) {
                          field.onChange(0)
                        } else {
                          const clamped = Math.max(0, Math.min(100, Math.round(n)))
                          field.onChange(clamped)
                        }
                      }}
                    />
                  )}
                />
                {markupValue > 0 && (
                  <p className="field-helper" style={{ marginTop: 8 }}>
                    <strong>Example:</strong> On a $285 urine hormone test with{' '}
                    {markupValue}% markup, the patient pays $
                    {(285 * (1 + markupValue / 100)).toFixed(2)} and your
                    practice earns ${(285 * markupValue / 100).toFixed(2)}.
                  </p>
                )}
              </Field>
            </FieldGroup>
            )}
          </Card>
        </div>
      </div>

      <div className="step-section" style={{ marginTop: 24 }}>
        <div className="pay-card">
          <div className="pay-card-heading">
            <svg
              className="pay-card-lock"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0110 0v4" />
            </svg>
            Secure card on file
          </div>
          <p className="pay-card-desc">
            {liteMode
              ? 'We keep your card on file for billing. $1 verification hold, refunded automatically within 3–5 business days.'
              : 'Required — verify with a $1 hold. Your card stays on file for billing.'}
          </p>
          <div className="pay-card-brands" aria-hidden="true">
            <img src="/visa.svg" alt="" className="pay-card-brand-icon" />
            <img src="/mastercard.svg" alt="" className="pay-card-brand-icon" />
            <img src="/amex.svg" alt="" className="pay-card-brand-icon" />
            <img src="/discover.svg" alt="" className="pay-card-brand-icon" />
          </div>
          {hasSavedCard ? (
            <div className="pay-card-saved">
              <span className="coupon-applied-pill">
                {formatBrand(stripeSaved.card.brand)} ••••{' '}
                {stripeSaved.card.last4} · exp{' '}
                {formatExpiry(
                  stripeSaved.card.expMonth,
                  stripeSaved.card.expYear,
                )}
              </span>
              <button
                type="button"
                className="coupon-remove"
                onClick={removeSavedCard}
              >
                Remove
              </button>
            </div>
          ) : (
            <>
              {!owner1Ready && (
                <div className="pay-card-prereq" role="note">
                  Add an owner's name and email on the Owners step first.
                </div>
              )}
              <button
                type="button"
                className="pay-card-cta"
                onClick={beginCardVerification}
                disabled={cardButtonDisabled}
              >
                {payBusy ? 'Starting…' : 'Add card for verification'}
              </button>
            </>
          )}
          <div className="pay-card-footer">
            <div className="pay-card-footer-left">
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0110 0v4" />
              </svg>
              PCI DSS COMPLIANT
            </div>
            <div className="pay-card-footer-right">
              <span>Powered by</span>
              <img
                src="/stripe-logo.png"
                alt="Stripe"
                className="pay-card-stripe"
              />
            </div>
          </div>
        </div>
        {paymentBlockError && (
          <div className="field-error" style={{ marginTop: 8 }}>
            {paymentBlockError}
          </div>
        )}
        {payError && (
          <div className="payment-note" style={{ marginTop: 12 }}>
            {payError}
          </div>
        )}
      </div>

      </Band>
      </StepLayout>

      {payModal && (liteMode ? (
        <StripeCardOnlyModal
          clientSecret={payModal.clientSecret}
          onSuccess={handleModalSuccess}
          onClose={() => setPayModal(null)}
        />
      ) : (
        <StripePaymentModal
          clientSecret={payModal.clientSecret}
          finalAmountCents={payModal.finalAmountCents}
          forcedMethod="card"
          onSuccess={handleModalSuccess}
          onClose={() => setPayModal(null)}
        />
      ))}

      <button type="submit" className="sr-only" tabIndex={-1}>
        Continue
      </button>
    </form>
  )
}
